/**
 * Clamp a requested raster output scale so the export stays within pixel
 * budgets. The scale never drops below 1 — the design canvas itself is the
 * fidelity floor; caps only shave off *extra* upscaling.
 *
 * Rationale: LCM's editor exports at 2× so display-sized canvases (e.g. a
 * 600×600 email image) stay crisp on retina screens. New-engine canvases are
 * often already retina-sized (they match the customer's @2x source assets),
 * so a blanket 2× would produce @4x rasters — 10MB+ PNGs that make saves
 * take 30s+. The caps express the real goal ("enough pixels for retina email
 * display") instead of a blind multiplier.
 */
export function clampOutputScale(args: {
  scale: number;
  width: number;
  height: number;
  /** Cap on output width in px (e.g. 2× the email display width). */
  maxOutputWidth?: number;
  /** Cap on total output pixels — backstop for very tall images. */
  maxOutputPixels?: number;
}): number {
  const { scale, width, height, maxOutputWidth, maxOutputPixels } = args;
  if (width <= 0 || height <= 0) return scale;
  let clamped = scale;
  if (maxOutputWidth !== undefined) {
    clamped = Math.min(clamped, maxOutputWidth / width);
  }
  if (maxOutputPixels !== undefined) {
    clamped = Math.min(clamped, Math.sqrt(maxOutputPixels / (width * height)));
  }
  return Math.max(1, clamped);
}
